import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { FiBarChart2, FiDownload } from 'react-icons/fi';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { reportService } from '../services/apiService';
import LoadingSpinner from '../components/LoadingSpinner';
import StatCard from '../components/StatCard';
import { formatCurrency } from '../utils/formatters';

const Reports = () => {
  const [currency, setCurrency] = useState('USD');

  const { data: valuationData, isLoading: valuationLoading } = useQuery(
    'valuation',
    reportService.getValuation
  );

  if (valuationLoading) return <LoadingSpinner message="Loading reports..." />;

  const valuation = valuationData?.data || {};
  const summary = valuation.summary || {};
  const items = valuation.items || [];

  // Choose which currency to display
  const isLrd = currency === 'LRD';
  const currencySymbol = isLrd ? 'L$' : '$';

  const totalCostValue = isLrd
    ? summary.total_cost_value_lrd ?? summary.total_cost_value ?? 0
    : summary.total_cost_value_usd ?? summary.total_cost_value ?? 0;

  const totalSalesValue = isLrd
    ? summary.total_sales_value_lrd ?? summary.total_sales_value ?? 0
    : summary.total_sales_value_usd ?? summary.total_sales_value ?? 0;

  const potentialProfit = isLrd
    ? summary.potential_profit_lrd ??
      summary.potential_profit ??
      totalSalesValue - totalCostValue
    : summary.potential_profit_usd ??
      summary.potential_profit ??
      totalSalesValue - totalCostValue;

  // Chart data (top 10 items)
  const topProducts = items.slice(0, 10).map((item) => ({
    name: item.product_name?.substring(0, 20) || 'Unknown',
    value: isLrd
      ? parseFloat(item.cost_value_lrd || 0)
      : parseFloat(item.cost_value_usd || item.cost_value || 0),
    sales: isLrd
      ? parseFloat(item.sales_value_lrd || 0)
      : parseFloat(item.sales_value_usd || item.sales_value || 0),
  }));

  const COLORS = ['#1a73e8', '#22c55e', '#f59e0b', '#ef4444', '#7c3aed'];

  // Category distribution
  const categoryData = items.reduce((acc, item) => {
    const category = item.category || 'Uncategorized';
    const value = isLrd
      ? parseFloat(item.cost_value_lrd || 0)
      : parseFloat(item.cost_value_usd || item.cost_value || 0);
    if (!acc[category]) {
      acc[category] = { name: category, value: 0 };
    }
    acc[category].value += value;
    return acc;
  }, {});

  const categoryChartData = Object.values(categoryData);

  // Format helper for cells
  const fmt = (usdVal, lrdVal) => {
    if (isLrd) {
      return `L$${parseFloat(lrdVal || 0).toFixed(2)}`;
    }
    return formatCurrency(parseFloat(usdVal || 0));
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Reports & Analytics</h1>
          <p className="page-subtitle">
            Insights into your inventory performance
          </p>
        </div>
        <div className="page-actions">
          {/* Currency toggle */}
          <div
            style={{
              display: 'flex',
              gap: '4px',
              background: '#f3f4f6',
              padding: '4px',
              borderRadius: '8px',
            }}
          >
            <button
              className={`btn ${currency === 'USD' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setCurrency('USD')}
              style={{ padding: '6px 16px', fontSize: '13px' }}
            >
              USD $
            </button>
            <button
              className={`btn ${currency === 'LRD' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setCurrency('LRD')}
              style={{ padding: '6px 16px', fontSize: '13px' }}
            >
              LRD L$
            </button>
          </div>
          <button className="btn btn-secondary">
            <FiDownload size={18} />
            Export
          </button>
        </div>
      </div>

      <div className="stats-grid">
        <StatCard
          icon={<FiBarChart2 size={24} />}
          value={summary.total_products || 0}
          label="Total Products"
          color="blue"
        />
        <StatCard
          icon={<FiBarChart2 size={24} />}
          value={`${currencySymbol}${parseFloat(totalCostValue).toFixed(2)}`}
          label={`Cost Value (${currency})`}
          color="orange"
        />
        <StatCard
          icon={<FiBarChart2 size={24} />}
          value={`${currencySymbol}${parseFloat(totalSalesValue).toFixed(2)}`}
          label={`Sales Value (${currency})`}
          color="green"
        />
        <StatCard
          icon={<FiBarChart2 size={24} />}
          value={`${currencySymbol}${parseFloat(potentialProfit).toFixed(2)}`}
          label={`Potential Profit (${currency})`}
          color="purple"
        />
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
          gap: '24px',
          marginBottom: '24px',
        }}
      >
        {/* Top Products Chart */}
        <div className="card">
          <div className="card-header">
            <h2 className="card-title">
              Top Products by Value ({currency})
            </h2>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={topProducts}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} />
              <YAxis />
              <Tooltip
                formatter={(value) => `${currencySymbol}${value.toFixed(2)}`}
              />
              <Bar
                dataKey="value"
                fill="#1a73e8"
                name={`Cost Value (${currency})`}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Category Distribution */}
        <div className="card">
          <div className="card-header">
            <h2 className="card-title">
              Inventory by Category ({currency})
            </h2>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={categoryChartData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) =>
                  `${name}: ${(percent * 100).toFixed(0)}%`
                }
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {categoryChartData.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={COLORS[index % COLORS.length]}
                  />
                ))}
              </Pie>
              <Tooltip
                formatter={(value) => `${currencySymbol}${value.toFixed(2)}`}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Detailed Valuation Table */}
      <div className="card">
        <div className="card-header">
          <h2 className="card-title">
            Inventory Valuation Details ({currency})
          </h2>
        </div>
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>Product</th>
                <th>Category</th>
                <th>Stock</th>
                <th>Cost Price</th>
                <th>Selling Price</th>
                <th>Cost Value</th>
                <th>Sales Value</th>
                <th>Potential Profit</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => {
                const costPrice = isLrd
                  ? item.cost_price_lrd
                  : item.cost_price_usd ?? item.cost_price;
                const sellingPrice = isLrd
                  ? item.selling_price_lrd
                  : item.selling_price_usd ?? item.selling_price;
                const costValue = isLrd
                  ? item.cost_value_lrd
                  : item.cost_value_usd ?? item.cost_value;
                const salesValue = isLrd
                  ? item.sales_value_lrd
                  : item.sales_value_usd ?? item.sales_value;
                const profit =
                  parseFloat(salesValue || 0) - parseFloat(costValue || 0);

                return (
                  <tr key={item.product_id}>
                    <td>
                      <strong>{item.product_name}</strong>
                    </td>
                    <td>{item.category || '-'}</td>
                    <td>{item.current_stock}</td>
                    <td>{fmt(costPrice, item.cost_price_lrd)}</td>
                    <td>{fmt(sellingPrice, item.selling_price_lrd)}</td>
                    <td>{fmt(costValue, item.cost_value_lrd)}</td>
                    <td>{fmt(salesValue, item.sales_value_lrd)}</td>
                    <td>
                      <span className="badge badge-success">
                        {currencySymbol}
                        {profit.toFixed(2)}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Reports;
